import gql from 'graphql-tag';
import graphqlClient from '../helpers/graphqlClient';
import { getNameAndEmailFilter } from '@/helpers/queryFilters';
import { stepQueries } from '@/helpers/graphql/fragments';
import { formatPricePears } from '@/helpers/utils';

export default new (class {
	async removeStudentsFromSimulation(studentIds) {
		let list = [];

		list.push(studentIds);

		if (Array.isArray(studentIds)) {
			list = studentIds;
		}

		return await graphqlClient.mutate({
			mutation: gql`
				mutation($ids: [Int]!) {
					deleteManyStudent(ids: $ids)
				}
			`,
			variables: {
				ids: list
			}
		});
	}

	async addStudents(item) {
		let list = [];

		list.push(item);

		if (Array.isArray(item)) {
			list = item;
		}

		return await graphqlClient.mutate({
			mutation: gql`
				mutation($list: [StudentAddInputObjectGraphType]) {
					addManyStudent(list: $list) {
						id
					}
				}
			`,
			variables: {
				list: list
			}
		});
	}

	async getStudentsIdsByUserData(whereFields) {
		let filters = [];
		Object.keys(whereFields).forEach(element => {
			filters.push(`${element}="${whereFields[element]}"`);
		});

		filters = filters.join('&&');

		return await graphqlClient.query({
			query: gql`
				query getStudentsByUserData($where: String!) {
					students(where: $where) {
						id
					}
				}
			`,
			variables: {
				where: filters
			},
			fetchPolicy: 'no-cache'
		});
	}

	/**
	 * Get simulations (a.k.a. students) of a user based on user's id.
	 *
	 * @param {integer} userId
	 * @param {integer|null} facilityId
	 * @returns {Promise}
	 */
	async getSimulationsByUser(userId, facilityId) {
		return await graphqlClient.query({
			query: gql`
				query students($where: String!) {
					simulations: students(where: $where, order: "id desc") {
						id
						simulationId
						isFavouriteForCurrentUser
						cmsCareerJobTitle {
							jobTitle
						}
						cmsLifeStage {
							name
						}
						sim: simulation {
							sessionName
							facility_ID
						}
						simulationTry
						availableCategoriesForCurrentLifeStage {
							name
							assetName
						}
						completedCategoriesForCurrentLifeStage {
							name
							assetName
						}
						completedCategories: completedCategoriesCountForCurrentLifeStage
						availableCategories: availableCategoriesCountForCurrentLifeStage
					}
				}
			`,
			fetchPolicy: 'no-cache',
			variables: {
				where: `userId = ${userId} && simulation.facility_id=${facilityId}`
			}
		});
	}
	/**
	 * Get users and their students with minimal information
	 * Like user's first and last name, student's favoriteIds (if presented) id and simId
	 *
	 * @param {integer|null} facilityID if Null is passed it means "No facility"
	 * @param {string} searchQuery string that should be included in the search.
	 * @param {object} pagination contains data for pagination like: skip, take and order
	 * @param {object} useFavorites contains data whether to get only favorites ones or all.
	 * @returns {Promise}
	 */
	async getStudentsBasedOnUsers(
		facilityID,
		searchQuery,
		pagination,
		useFavorites
	) {
		const usersFilters = [];
		const { sort, skip, take } = pagination;
		const { favoritesOnly, userId } = useFavorites || {};

		if (searchQuery) {
			usersFilters.push(
				getNameAndEmailFilter(searchQuery, null, 'emailAddress')
			);
		}
		// we need only students, not other users
		usersFilters.push('roleId = "7"');
		// Filter those uses that have facilityId = to the currently selected facility
		usersFilters.push(
			`students.Count(it.simulation.facility_ID = ${facilityID}) > 0`
		);

		// Get the users that are favorites for currently logged user
		// Directly getting it from userFavoriteStudents causes duplications
		// and results in a hard maintenance. Also, because it is used for a specific component
		// instead of remapping the data we can directly pass the data to that component.
		if (favoritesOnly) {
			usersFilters.push(
				`students.Count(it.userFavoriteStudents.Count(it.userId = ${userId} && it.isDeleted != "true") > 0) > 0`
			);
		}

		return await graphqlClient.query({
			query: gql`
				query users(
					$order: String!
					$usersWhere: String!
					$userCareersWhere: String!
					$skip: Int!
					$take: Int!
				) {
					users: users(
						where: $usersWhere
						order: $order
						skip: $skip
						take: $take
					) {
						id
						firstName
						lastName
						emailAddress
						career: careers(where: $userCareersWhere) {
							id
							career_ID
						}
						simulations: students(order: "id desc") {
							id
							simulationId
							sim: simulation {
								facility_ID
							}
							isFavouriteForCurrentUser
							favoriteIds: userFavoriteStudents {
								id
							}
						}
					}
					allStudents: getCountUser(where: $usersWhere)
				}
			`,
			variables: {
				order: sort,
				skip: skip,
				take: take,
				usersWhere: usersFilters.join('&&'),
				userCareersWhere: `facility_ID = ${facilityID}`
			},
			fetchPolicy: 'no-cache'
		});
	}

	/**
	 * Add or remove records from userFavoriteStudents.
	 * If add is needed pass an Array of objects where object have a signature like: {userId:<value>,studentId<value>}
	 * If remove is needed, pass an array of integers - userFavoriteStudents ids, like: [<int>,<int>...<int>]
	 * Also pass isAlreadyFavorite to true in order to trigger the right mutation
	 *
	 * @param {string|array} data id or ids that should be added/removed
	 * @param {boolean} isAlreadyFavorite Set to true if you want to remove passed ids.
	 * @returns {Promise}
	 */
	async toggleFavoriteStudentsForUser(data, isAlreadyFavorite) {
		let list = [];

		list.push(data);

		if (Array.isArray(data)) {
			list = data;
		}

		if (isAlreadyFavorite) {
			return await graphqlClient.mutate({
				mutation: gql`
					mutation($list: [Int]!) {
						deleteManyUserFavoriteStudent(ids: $list)
					}
				`,
				variables: {
					list
				}
			});
		}

		return await graphqlClient.mutate({
			mutation: gql`
				mutation($list: [UserFavoriteStudentAddInputObjectGraphType]!) {
					addManyUserFavoriteStudent(list: $list) {
						id
					}
				}
			`,
			variables: {
				list
			}
		});
	}

	async checkBeforeUpdate(
		studentId,
		lifeStageId,
		subCategoriesIds,
		expenseCategoryId,
		shouldReturnIds
	) {
		const filters = [];

		filters.push(`studentId = ${studentId}`);
		filters.push(`lifeStage_ID="${lifeStageId}"`);
		//at this point - criteria for category and subcategories are the same
		const categoryFilters = [...filters];
		const subcategories = [];

		// combine all ids into one query
		subCategoriesIds.forEach(item => {
			subcategories.push(`expenseSubCategory_Id="${item}"`);
		});

		filters.push(`(${subcategories.join('||')})`);

		// set Expense categoryId for skip category check
		categoryFilters.push(`expenseCategory_ID="${expenseCategoryId}"`);

		return await new Promise(resolve => {
			graphqlClient
				.query({
					query: gql`
						query checkSubCategories($where: String!, $categoryWhere: String!) {
							subCategories: studentExpenseSubCategoryExpenditures(
								where: $where
							) {
								id
								expenseSubCategory_Id
								lifeStage_ID
								amountSpent
							}
							skippedCategory: studentExpenseCategories(where: $categoryWhere) {
								id
								noPurchaseIsMade
							}
						}
					`,
					variables: {
						where: filters.join('&&'),
						categoryWhere: categoryFilters.join('&&')
					},
					fetchPolicy: 'no-cache'
				})
				.then(({ data }) => {
					if (shouldReturnIds) {
						const resolvedObject = {
							subcategories: data.subCategories.map(i => i.id),
							skippedCategory: data.skippedCategory[0]
								? data.skippedCategory[0].id
								: 0
						};
						resolve(resolvedObject);
					}
					// if Some of the arrays have something,
					// then user is not able to proceed correctly
					// Either the category or subcategories are already selected
					if (data.subCategories.length || data.skippedCategory.length) {
						resolve(false);
					}

					resolve(true);
				});
		});
	}

	/**
	 * Validate whether the given step is already completed or not
	 * Uses fragments defined in @/helpers/constants.
	 * Each fragment contains property, called `returnValue<boolean>` which determines
	 * The return value of the function. If set to true - the function will resolve the response's content
	 * otherwise it will be resolved as true/false
	 *
	 * @param {String} step - string representation (constant) defined in constants/profileCreationSteps
	 * @param {Number} studentId - the Id of the student in question
	 * @returns {Boolean|Array}
	 */
	async isProfileCreationStepCompleted(step, studentId) {
		const filters = [];

		// use fragment's query for current check, based on provided step
		const stepQuery = stepQueries[step].query;

		// get the whereField or if nothing is presented, use "studentId" by default.
		// A lof of queries uses "studentId"
		const whereField = stepQueries[step].whereField || 'studentId';

		filters.push(`${whereField} = ${studentId}`);

		return await new Promise(resolve => {
			graphqlClient
				.query({
					query: gql`
						query ProfileCreationStepCompletionCheck($where: String!) {
							${stepQuery}
						}
					`,
					variables: {
						where: filters.join('&&')
					},
					fetchPolicy: 'no-cache'
				})
				.then(({ data }) => {
					// if returnValue is specified and set to true
					// the result of the response will be returned
					if (stepQueries[step].returnValue) {
						resolve(data.step);
					}

					// step in data is set as alias in fragments
					// Used to unify the code here
					if (data.step.length) {
						resolve(true);
					}

					// resolve as false in any other case
					resolve(false);
				});
		});
	}

	async checkBudgetForStudent(studentId, lifeStageId, goalsIds) {
		const filters = [];
		const savingsFilter = [];
		const savingsClause = [];

		filters.push(`studentId=${studentId}`);
		filters.push(`lifeStage_ID="${lifeStageId}"`);

		goalsIds.forEach(id => {
			savingsClause.push(`studentGoalId = ${id}`);
		});

		savingsFilter.push(`lifeStage_ID="${lifeStageId}"`);
		savingsFilter.push(`(${savingsClause.join('||')})`);

		return await new Promise(resolve => {
			graphqlClient
				.query({
					query: gql`
						query checkBudgetForStudent(
							$where: String!
							$savingsWhere: String!
						) {
							categoriesBudgets: studentExpenseCategoryBudgets(where: $where) {
								id
								expenseCategory_Id
								lifeStage_ID
								studentId
								cmsExpenseCategory {
									name
									defaultName
								}
							}
							debts: studentDebtBudgets(where: $where) {
								id
								budget
								debtType
								lifeStage_ID
							}
							savings: studentGoalSavings(where: $savingsWhere) {
								id
								studentGoalId
								lifeStage_ID
								amount
							}
						}
					`,
					variables: {
						where: filters.join('&&'),
						savingsWhere: savingsFilter.join('&&')
					},
					fetchPolicy: 'no-cache'
				})
				.then(({ data }) => {
					resolve(data);
				});
		});
	}

	async getUsersForReport(filters, lifeStages, paginationData) {
		let usersFilters = [];
		let studentsFilters = filters;
		let stageFilters = '';

		// Since StageFilters are coming as array we join them to simply replace them later
		stageFilters = lifeStages.join('||');
		// We don't need demo records
		studentsFilters.push('isDemo != true');

		// set base users filters as template, where
		// $FILTERS and $LIFE_STAGE will be replaced later with correct data
		let usersWhere =
			'roleId="7" && students.Count($FILTERS && ($LIFE_STAGES)) > 0';

		// loop through students' filters and add an `it` in front of the filter
		// this is needed in order for gql to work properly with `.Count`
		// ex: students.Count(it.simulation.sessionName = "Lorem Ipsum");
		studentsFilters.forEach(filter => {
			filter = `it.${filter}`;
			usersFilters.push(filter);
		});

		// Add life stage filters for students after the generation of users' filters
		studentsFilters.push(`(${stageFilters})`);

		// simply replace $FILTERS with already generated array of filters as string
		usersWhere = usersWhere.replace('$FILTERS', usersFilters.join('&&'));

		// Now we can replace lifeStage_ID with "it" in front of it
		// This is needed in order for lifeStage_id to be used correctly in Users' filters
		stageFilters = stageFilters.replaceAll('lifeStage_ID', 'it.lifeStage_ID');

		// Replace Placeholder with correct string
		// NOTE: The brackets are in the usersWhere template
		usersWhere = usersWhere.replace('$LIFE_STAGES', stageFilters);

		return await graphqlClient.query({
			query: gql`
				query studentsReport(
					$where: String!
					$studentsWhere: String!
					$skip: Int!
					$take: Int!
				) {
					users(where: $where, skip: $skip, take: $take) {
						id
						firstName
						lastName
						simulations: students(where: $studentsWhere, order: "id desc") {
							id
							firstName
							lastName
							userId
							simulationId
							simulation {
								id
								sessionName
							}
						}
					}
					usersCount: getCountUser(where: $where)
				}
			`,
			variables: {
				where: usersWhere,
				studentsWhere: studentsFilters.join('&&'),
				skip: paginationData.recordsPerPage * (paginationData.currentPage - 1),
				take: paginationData.recordsPerPage
			},
			fetchPolicy: 'no-cache'
		});
	}

	async getSimulationDataByStudent(sim, lifeStageId) {
		const filters = [];

		filters.push(`id=${sim.studentId}`);
		filters.push(`simulationId=${sim.id}`);

		return await graphqlClient.query({
			query: gql`
				query SimulationForReport($where: String!, $lifeStage: String!) {
					simulation: students(where: $where, order: "id desc") {
						id
						isAvatarSelected
						simulationId
						simulationTry
						userId
						answeredQuestions: studentQuestionAnswers {
							id
						}
						interests: studentInterests {
							id
						}
						career: cmsCareerJobTitle {
							jobTitle
						}
						education: cmsEducation {
							name
						}
						goals: studentGoals {
							id
						}
						simulation {
							id
							sessionName
							simulationType
						}
						studentAtAGlanceReport(where: $lifeStage) {
							completed: completedExpenseCategories
							available: availableExpenseCategories
							data: datas {
								actual
								budgeted
								id: expenseCategory_ID
								name: expenseCategoryName
							}
						}
					}
				}
			`,
			variables: {
				where: filters.join('&&'),
				lifeStage: lifeStageId
			},
			fetchPolicy: 'no-cache'
		});
	}

	/**
	 * Get report information for all tries of a user based on simulation's data (userId)
	 * And simulation's id.
	 *
	 * @param {Object} sim
	 * @param {String} lifeStageId
	 * @returns {Promise}
	 */
	async getSimulationTriesFor(sim, lifeStageId) {
		const filters = [];

		filters.push(`simulationId = ${sim.id}`);
		filters.push(`userId = ${sim.userId}`);

		return await new Promise(resolve => {
			graphqlClient
				.query({
					query: gql`
						query simulationTriesData($where: String!, $lifeStage: String!) {
							simulations: students(where: $where) {
								simulationId
								simulation {
									sessionName
								}
								simulationTry
								report: studentAtAGlanceReport(where: $lifeStage) {
									data: datas {
										actual
										budgeted
										id: expenseCategory_ID
										name: expenseCategoryName
									}
								}
							}
						}
					`,
					variables: {
						where: filters.join('&&'),
						lifeStage: lifeStageId
					},
					fetchPolicy: 'no-cache'
				})
				.then(({ data }) => {
					const mappedData = data.simulations
						.map(item => {
							return {
								report: item.report[0].data.map(i => {
									return {
										...i,
										value: formatPricePears(i.actual, i.budgeted)
									};
								}),
								try: item.simulationTry
							};
						})
						.reverse();

					resolve(mappedData);
				});
		});
	}

	async updateTooltipStatus(tooltipsStatusInfo) {
		const { data } = await graphqlClient.mutate({
			mutation: gql`
				mutation($update: StudentInputObjectGraphType!) {
					updateIgnoreNullValuesStudent(update: $update) {
						id
					}
				}
			`,
			variables: {
				update: tooltipsStatusInfo
			}
		});

		if (data) {
			return true;
		}

		return false;
	}
})();
